import { useArcPath } from '~/components/common/chart/hooks/useArcPath';

import {
  CIRCLE_PROGRESS_DEFAULT_PROGRESS_COLOR,
  CIRCLE_PROGRESS_DEFAULT_SIZE,
  CIRCLE_PROGRESS_DEFAULT_SUFFIX,
  CIRCLE_PROGRESS_DEFAULT_THICKNESS,
  CIRCLE_PROGRESS_DEFAULT_TRACK_COLOR,
  CIRCLE_PROGRESS_LABEL_COLOR,
  CIRCLE_PROGRESS_LABEL_FONT_SIZE,
  CIRCLE_PROGRESS_LABEL_FONT_WEIGHT,
  CIRCLE_PROGRESS_START_ANGLE,
  CIRCLE_PROGRESS_SUFFIX_DX,
  CIRCLE_PROGRESS_SUFFIX_DY,
  CIRCLE_PROGRESS_SUFFIX_FONT_SIZE,
  CIRCLE_PROGRESS_SUFFIX_FONT_WEIGHT,
  TAU,
} from './constants';

interface FontStyle {
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  dx?: number;
  dy?: number;
}

interface CircleProgressProps {
  size?: number;
  percent?: number;
  thickness?: number;
  trackColor?: string;
  progressColor?: string;
  value?: string | number;
  suffix?: string | null;
  hasRoundCap?: boolean;
  labelFontStyle?: FontStyle;
  suffixFontStyle?: FontStyle;
}

const DEFAULT_LABEL_FONT_STYLE: FontStyle = {
  fontSize: CIRCLE_PROGRESS_LABEL_FONT_SIZE,
  fontWeight: CIRCLE_PROGRESS_LABEL_FONT_WEIGHT,
  color: CIRCLE_PROGRESS_LABEL_COLOR,
};

const DEFAULT_SUFFIX_FONT_STYLE: FontStyle = {
  fontSize: CIRCLE_PROGRESS_SUFFIX_FONT_SIZE,
  fontWeight: CIRCLE_PROGRESS_SUFFIX_FONT_WEIGHT,
  color: CIRCLE_PROGRESS_LABEL_COLOR,
  dx: CIRCLE_PROGRESS_SUFFIX_DX,
  dy: CIRCLE_PROGRESS_SUFFIX_DY,
};

const CircleProgress = ({
  size = CIRCLE_PROGRESS_DEFAULT_SIZE,
  percent = 0,
  value,
  suffix = CIRCLE_PROGRESS_DEFAULT_SUFFIX,
  thickness = CIRCLE_PROGRESS_DEFAULT_THICKNESS,
  trackColor = CIRCLE_PROGRESS_DEFAULT_TRACK_COLOR,
  progressColor = CIRCLE_PROGRESS_DEFAULT_PROGRESS_COLOR,
  hasRoundCap = true,
  labelFontStyle,
  suffixFontStyle,
}: CircleProgressProps) => {
  const radius = size / 2;
  const outerRadius = radius;
  const innerRadius = Math.max(0, outerRadius - thickness);
  const cornerRadius = hasRoundCap ? thickness / 2 : 0;

  const startAngle = CIRCLE_PROGRESS_START_ANGLE;
  const arcGenerator = useArcPath({ innerRadius, outerRadius, cornerRadius });

  const backgroundArc = arcGenerator({ startAngle, endAngle: startAngle + TAU });
  const foregroundArc = arcGenerator({ startAngle, endAngle: startAngle + (percent / 100) * TAU });

  const mergedLabelFontStyle = { ...DEFAULT_LABEL_FONT_STYLE, ...labelFontStyle };
  const mergedSuffixFontStyle = { ...DEFAULT_SUFFIX_FONT_STYLE, ...suffixFontStyle };

  return (
    <svg
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
    >
      <g transform={`translate(${size / 2},${size / 2})`}>
        {backgroundArc && <path d={backgroundArc} fill={trackColor} />}
        {foregroundArc && <path d={foregroundArc} fill={progressColor} />}
        <text textAnchor="middle" dominantBaseline="central" aria-hidden="true">
          <tspan
            fontSize={mergedLabelFontStyle.fontSize}
            fontWeight={mergedLabelFontStyle.fontWeight}
            fill={mergedLabelFontStyle.color}
          >
            {value ?? percent}
          </tspan>
          {suffix ? (
            <tspan
              fontSize={mergedSuffixFontStyle.fontSize}
              fontWeight={mergedSuffixFontStyle.fontWeight}
              fill={mergedSuffixFontStyle.color}
              dx={mergedSuffixFontStyle.dx}
              dy={mergedSuffixFontStyle.dy}
            >
              {suffix}
            </tspan>
          ) : null}
        </text>
      </g>
    </svg>
  );
};

export { CircleProgress };
